/**
 * TAN-8655 — Catálogo único de claves i18n de títulos de página (WCAG 2.4.2).
 *
 * Los dos `routesConfig.js` (estándar y Edelvives) referencian estas claves desde su campo
 * `titleKey`, y `RouteChangeHandler` las traduce para escribir `document.title` en cada cambio
 * de ruta. Al estar centralizadas aquí, el test de registro
 * (`__tests__/routeTitles.registry.test.ts`) puede comprobar que toda clave referenciada existe
 * en los ocho bundles `titles.json`.
 *
 * El valor es siempre el string completo `titles:<Nombre>`: el namespace de i18next es la clave
 * raíz del JSON (`{ "titles": { ... } }`), no el nombre del fichero.
 */

export const TITLE_KEYS = {
  // ---------------------------------------------------------------------------
  // Compartidas: las usan LOS DOS routers (app/router y edelvives/router).
  // ---------------------------------------------------------------------------
  Home: 'titles:Home',
  Today: 'titles:Today',
  MyProgress: 'titles:MyProgress',
  // `/course/:guid` sin sufijo ES la pestaña de panel (`stream: ''` en `useHeaderCourse`),
  // así que el título es la etiqueta de esa pestaña (`course:Stream`), no un genérico.
  CourseStream: 'titles:CourseStream',
  CourseHome: 'titles:CourseHome',
  CourseProgram: 'titles:CourseProgram',
  CourseGrades: 'titles:CourseGrades',
  CourseTraining: 'titles:CourseTraining',
  CourseResources: 'titles:CourseResources',
  TrainingPractice: 'titles:TrainingPractice',
  Lesson: 'titles:Lesson',
  NewActivity: 'titles:NewActivity',
  Activity: 'titles:Activity',
  Assessment: 'titles:Assessment',
  Question: 'titles:Question',
  Results: 'titles:Results',
  Kanban: 'titles:Kanban',
  Calendar: 'titles:Calendar',
  Tasks: 'titles:Tasks',
  Profile: 'titles:Profile',
  Projection: 'titles:Projection',
  Viewer: 'titles:Viewer',
  Tools: 'titles:Tools',
  TeacherTools: 'titles:TeacherTools',
  TeacherToolsHistory: 'titles:TeacherToolsHistory',
  TeacherToolSetup: 'titles:TeacherToolSetup',
  TeacherTool: 'titles:TeacherTool',
  Radar: 'titles:Radar',
  Students: 'titles:Students',
  StudentProgress: 'titles:StudentProgress',
  StudentKcProgress: 'titles:StudentKcProgress',
  Analytics: 'titles:Analytics',
  Practice: 'titles:Practice',
  Login: 'titles:Login',
  Signup: 'titles:Signup',
  /* eslint-disable sonarjs/no-hardcoded-passwords -- son claves i18n de títulos de pantalla, no credenciales */
  RecoverPassword: 'titles:RecoverPassword',
  ResetPassword: 'titles:ResetPassword',
  /* eslint-enable sonarjs/no-hardcoded-passwords */
  License: 'titles:License',
  Privacy: 'titles:Privacy',
  Terms: 'titles:Terms',
  Error: 'titles:Error',
  Redirecting: 'titles:Redirecting',
  TeamsConfig: 'titles:TeamsConfig',
  NotFound: 'titles:NotFound',

  // ---------------------------------------------------------------------------
  // Exclusivas del ROUTER ESTÁNDAR (`src/app/router/routesConfig.js`).
  // No existen como ruta en Edelvives: no las reutilices allí.
  // ---------------------------------------------------------------------------
  Library: 'titles:Library',
  // `Content` titula solo el visor de recurso, que se llama por el nombre del recurso. Crear y
  // editar material son pantallas distintas con rótulo propio (`content:Add material` /
  // `content:Edit material`) y clave propia: con una sola, editar y crear decían lo mismo.
  Content: 'titles:Content',
  AddMaterial: 'titles:AddMaterial',
  EditMaterial: 'titles:EditMaterial',
  // Solo el router estándar tiene `EDIT_ITEM`; en Edelvives únicamente se crea (`NewActivity`).
  EditActivity: 'titles:EditActivity',
  ClassroomResources: 'titles:ClassroomResources',
  CrossGradebook: 'titles:CrossGradebook',
  ResultsStudent: 'titles:ResultsStudent',
  ResultsQuestion: 'titles:ResultsQuestion',
  Credits: 'titles:Credits',
  Status: 'titles:Status',
  TestComponent: 'titles:TestComponent',

  // ---------------------------------------------------------------------------
  // Exclusivas del ROUTER DE EDELVIVES (`src/edelvives/router/routesConfig.js`).
  // No existen como ruta en el router estándar: `Evaluation` y `EditEvaluation` son el
  // flujo de pruebas de Edelvives, NO el equivalente genérico de `Assessment`.
  // ---------------------------------------------------------------------------
  Evaluation: 'titles:Evaluation',
  // Las cuatro rutas de creación/edición de pruebas (con y sin IA) pintan la misma pantalla, y su
  // encabezado es `evaluations:Evaluations generator` tanto al crear como al editar: no hay dos
  // títulos que repartir, había uno mal puesto («Editar prueba» mientras se creaba).
  EvaluationsGenerator: 'titles:EvaluationsGenerator',
  ClassroomUsers: 'titles:ClassroomUsers',
  About: 'titles:About',
  Support: 'titles:Support',
  AIPolicy: 'titles:AIPolicy',
  Desktop: 'titles:Desktop',
  // `/preschool-resources`: pantalla propia, no la pestaña de recursos del curso.
  PreschoolResources: 'titles:PreschoolResources',
} as const;

/** Valor i18n del catálogo (p. ej. `'titles:Home'`). Es el tipo del campo `titleKey` de `RouteConfig`. */
export type TitleKey = (typeof TITLE_KEYS)[keyof typeof TITLE_KEYS];
